import type { CarbonIconType } from '@carbon/icons-react';
import { CloudOffline, Locked } from '@carbon/icons-react';
import { useTranslation } from '@pos/i18n';
import { useTransientMessage } from '../shared/useTransientMessage';
import { useHoReachable } from '../HoStatus';
import './PosActionButton.scss';

export type PosActionRole = 'send' | 'pay' | 'bill' | 'danger' | 'tool';

export interface PosActionButtonProps {
  role: PosActionRole;
  label: string;
  icon?: CarbonIconType;
  requiresManager?: boolean;
  /** Disabled with the CloudOffline icon while HO is unreachable; tapping explains why (§9.7). */
  requiresHo?: boolean;
  /**
   * Looks disabled, but a tap explains why with this text instead of acting, e.g. "Put a member on the order first". Like
   * `requiresHo`, it tells the cashier what is missing rather than hiding the action.
   */
  blockedReason?: string;
  disabled?: boolean;
  badge?: string | number;
  /** Fill the width of the container (rails, forms, tender grids). */
  /** For a button that opens or closes something (More): sets aria-expanded. */
  expanded?: boolean;
  fill?: boolean;
  /** For layout of the wrapper only, e.g. flex sizing in a row of actions. */
  className?: string;
  onPress(): void;
}

export function PosActionButton({
  role,
  label,
  icon: Icon,
  requiresManager,
  requiresHo,
  blockedReason,
  disabled,
  badge,
  expanded,
  fill,
  className,
  onPress,
}: PosActionButtonProps) {
  const { t } = useTranslation();
  const hoReachable = useHoReachable();
  const reason = useTransientMessage();
  const blockedByHo = Boolean(requiresHo && !hoReachable);
  const blocked = blockedByHo || Boolean(blockedReason);

  return (
    <span className={`pos-action${fill ? ' pos-action--fill' : ''}${className ? ` ${className}` : ''}`}>
      <button
        type="button"
        className={`pos-action__button pos-action__button--${role}`}
        disabled={disabled}
        // aria-disabled, not disabled, so the tap still registers and can explain itself.
        aria-disabled={blocked || undefined}
        aria-expanded={expanded}
        onClick={() => (blocked ? reason.show() : onPress())}
        onBlur={reason.clear}
      >
        {Icon && <Icon size={24} aria-hidden="true" />}
        <span className="pos-action__label">{label}</span>
        {badge !== undefined && <span className="pos-action__badge">{badge}</span>}
        {requiresManager && <Locked size={16} className="pos-action__corner" aria-hidden="true" />}
        {blockedByHo && <CloudOffline size={16} className="pos-action__corner" aria-hidden="true" />}
      </button>
      {/* A persistent live region, so the reason is announced when it appears; it is cleared after a few seconds. */}
      <span className="pos-action__reason" role="status">
        {blocked && reason.shown ? (blockedByHo ? t('requiresHo.reason') : blockedReason) : ''}
      </span>
    </span>
  );
}
